import { Avatar, Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  BankAccountMembershipMembersDatatableDocument,
  ProjectionKind,
  useBankAccountMembershipMembersDatatable,
} from 'generated/resources/datatable.graphql'
import {
  BankingBankAccountFragment,
  RemoveBankAccountMembershipMemberDocument,
} from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
// eslint-disable-next-line factorial/no-design-system-long-imports
import Popover from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

interface Props {
  bankAccount: BankingBankAccountFragment
  notRemomableAccessIds?: number[]
  onAddClick: () => void
}

export const SidepanelMemberships = ({ bankAccount, notRemomableAccessIds, onAddClick }: Props) => {
  const { company } = useCurrentSession()

  const [removeMembership, { loading }] = useMutation(RemoveBankAccountMembershipMemberDocument, {
    refetchQueries: [BankAccountMembershipMembersDatatableDocument],
    extractorFn: (data) => data.bankingMutations.upsertBankingBankAccountMembership,
  })

  const source = useBankAccountMembershipMembersDatatable(
    [
      {
        identifier: 'employee',
        colSpan: 9,
        title: i18n.t('billing.subscription.plans.expenses.columns.user'),
        kind: ProjectionKind.Custom,
        template: '{{ access.fullName }}',
        render: ({ access }) => {
          return (
            <Box flexDirection="row" alignItems="center" gap="s16" minWidth="s96">
              <Avatar size="s40" url={buildUrl(access?.avatar)} label={access?.fullName || ''} />
              <Text.Normal size="200" weight="medium">
                {access?.fullName || ''}
              </Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'addedOn',
        colSpan: 2,
        title: i18n.t('billing.subscription.plans.expenses.columns.added_on'),
        kind: ProjectionKind.Custom,
        template: '{{ createdAt }}',
        render: ({ createdAt }) => {
          return (
            <Text.Normal size="200" weight="medium">
              {createdAt ? dateToString(stringToDate(createdAt, 'backend_datetime'), 'ui') : ''}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'options',
        colSpan: 1,
        kind: ProjectionKind.Decorative,
        render: (membership) => {
          if (notRemomableAccessIds && !notRemomableAccessIds.includes(membership.access.id)) {
            return (
              <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
                <Popover type="list">
                  <Popover.Trigger>
                    <Button.Decorative type="secondary" iconName="ellipsis" />
                  </Popover.Trigger>
                  <Popover.Content side="bottom" align="end">
                    <Popover.TouchableOption
                      onClick={() =>
                        removeMembership({
                          variables: {
                            accessIds: [membership.access.id],
                            companyId: company.id,
                            bankAccountId: bankAccount.id,
                          },
                        })
                      }
                      title={i18n.t('banking.accounts.side_panel.remove_membership')}
                      danger
                      disabled={loading}
                    />
                  </Popover.Content>
                </Popover>
              </Box>
            )
          }
        },
      },
    ],
    {
      variables: {
        bankAccountId: bankAccount.id,
      },
    }
  )

  return (
    <Box gap={'s4'}>
      <Text.Normal color="grey1000" weight="medium">
        {i18n.t('banking.accounts.finish_wizard.step_3.title')}
      </Text.Normal>
      <Text color="grey800" weight="normal" size="200">
        {i18n.t('banking.accounts.finish_wizard.step_3.subtitle')}
      </Text>
      <Box flexDirection="row" justifyContent="spaceBetween" width="full">
        <Button disabled={loading} type="primary" iconName="add" onClick={onAddClick}>
          {i18n.t('banking.accounts.side_panel.add_membership')}
        </Button>
      </Box>
      {bankAccount.bankAccountMembership?.id && (
        <Box>
          <DataTable
            autoWidth
            persistFilters={false}
            source={source}
            pagination={{
              kind: 'offset',
              itemsPerPage: 5,
            }}
            disableDefaultActions
          />
        </Box>
      )}
    </Box>
  )
}
